import { AccountType } from "users/account-types/account-type";
import { User } from "users/user";
import { Masked } from "work/masked-fields";
import { ProposalAction } from "work/proposals/__constants/proposal-lifecycle";
import Proposal from "work/proposals/proposal";

User.prototype.canShare = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.canEdit(proposal)) return true;
  if (this.canRedline(proposal)) return true;
  if (
    proposal.clientReviewers
      .concat(proposal.vendorReviewers)
      .some((reviewer) => reviewer.userId.isEqualTo(this.id) && reviewer.isEditing)
  )
    return true;
  return false;
};

User.prototype.canEdit = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Edit].some((id) => id.isEqualTo(this.id));
};

User.prototype.canRevise = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Revise].some((id) => id.isEqualTo(this.id));
};

User.prototype.canRedline = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.canRevise(proposal)) return true;

  return (
    proposal.clientReviewers.some((r) => r.isEditing && r.userId.isEqualTo(this.id)) ||
    proposal.vendorReviewers.some((r) => r.isEditing && r.userId.isEqualTo(this.id))
  );
};

User.prototype.canApprove = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (!this?.id) return false;
  if (
    Masked.isMasked(proposal.workAgreement.conflictsCheckWaived) ||
    Masked.isMasked(proposal.workAgreement.conflicts) ||
    (!proposal.workAgreement.conflictsCheckWaived.value && proposal.workAgreement.conflicts.length === 0)
  ) {
    return false;
  }

  return proposal.availableActions[ProposalAction.Approve].some((id) => id.isEqualTo(this.id));
};

User.prototype.canReject = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Reject].some((id) => id.isEqualTo(this.id));
};

User.prototype.isClient = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (Masked.isMasked(proposal.clientLeader) || Masked.isMasked(proposal.vendorLeader)) return false;
  return proposal.clientLeader?.userId.isEqualTo(this.id) || false;
};

User.prototype.isAssociatedWithClient = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.isClient(proposal)) return true;
  if (proposal.clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(this.id))) return true;
  return false;
};

User.prototype.isVendor = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (Masked.isMasked(proposal.vendorLeader) || Masked.isMasked(proposal.clientLeader)) return false;
  return proposal.vendorLeader?.userId.isEqualTo(this.id) || false;
};

User.prototype.isAssociatedWithVendor = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (this.isVendor(proposal)) return true;
  if (proposal.additionalVendors?.some((vendor) => vendor.userId.isEqualTo(this.id))) return true;
  if (proposal.vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(this.id))) return true;
  return false;
};

User.prototype.isLatestSubmittingParty = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (proposal.supersedes || proposal.partyRevision) {
    const latestSubmittingParty = proposal.latestSubmittingParty;
    if (latestSubmittingParty) {
      return latestSubmittingParty === this.currentParty(proposal);
    }
  }

  return this.isInCreatorParty(proposal);
};

User.prototype.currentParty = function (proposal: Proposal | null | undefined): AccountType | undefined {
  if (!proposal) return undefined;
  if (proposal.currentUser) if (this.isAssociatedWithClient(proposal)) return AccountType.Client;
  if (this.isAssociatedWithVendor(proposal)) return AccountType.Vendor;
  return undefined;
};

User.prototype.isInCreatorParty = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;

  if (Masked.isMasked(proposal.clientLeader)) return false;
  if (Masked.isMasked(proposal.vendorTeam)) return false;
  // No proposal fields are maskable by submitters

  if (proposal.creator?.userId.isEqualTo(proposal.currentUser.id)) return true;

  const creatorIsClient = proposal.clientLeader?.userId.isEqualTo(proposal.creator?.userId);
  if (creatorIsClient) {
    return proposal.clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(proposal.currentUser.id));
  }

  if (Masked.isMasked(proposal.vendorTeam)) return false;
  const creatorIsTeamLeader = proposal.vendorTeam?.leader?.userId.isEqualTo(proposal.creator?.userId);
  if (creatorIsTeamLeader) {
    return proposal.vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(proposal.currentUser.id));
  }
  return false;
};

User.prototype.canHire = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  return proposal.availableActions[ProposalAction.Hire].some((id) => id.isEqualTo(this.id));
};

User.prototype.hasApproved = function (proposal: Proposal | null | undefined): boolean {
  if (!proposal) return false;
  if (proposal.availableActions[ProposalAction.Approve].some((id) => id.isEqualTo(this.id))) return false;
  if (this.isAssociatedWithClient(proposal)) return proposal.isApprovedByClient;
  if (this.isAssociatedWithVendor(proposal)) return proposal.isApprovedByTeamLeader;
  return false;
};
